'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import type { Route } from 'next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { toast } from '@/hooks/use-toast'
import {
  AlertTriangle,
  ArrowLeft,
  Cake,
  Calendar,
  CalendarClock,
  Check,
  CreditCard,
  Eye,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  Mic,
  Pencil,
  Phone,
  PhoneCall,
  Sparkles,
  UserCheck,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Textarea } from '@/components/ui/textarea'
import { CallDetailDialog } from '@/features/cases/components/call-detail-dialog'
import { CaseAnalysis } from '@/features/cases/components/case-analysis'
import { CaseDocuments } from '@/features/cases/components/case-documents'
import { CaseGoals } from '@/features/cases/components/case-goals'
import { CasePrescription } from '@/features/cases/components/case-prescription'
import { CaseUploadLinks } from '@/features/cases/components/case-upload-links'
import { CaseStatusBadge } from '@/features/cases/components/case-status-badge'
import { FlowBadge } from '@/features/cases/components/flow-badge'
import { SERVICE_TYPE_TO_FLOW } from '@/features/cases/hooks/use-my-case-detail'
import { ADVISOR_UNASSIGNED_VALUE, AdvisorCombobox } from '@/components/patterns/advisor-combobox'
import { useAdminUsers } from '@/features/admin'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import { useMe } from '@/features/session'
import {
  useCallStatus,
  useCalls,
  useCaseDetail,
  useCaseDocumentList,
  useCaseGoals,
  useCaseHoldings,
  useCasePrescriptionSteps,
  useCaseTrades,
} from '@/features/cases/hooks/use-case-detail'
import {
  useAssignAdvisor,
  useClaimCase,
  useCreateCase,
  usePublishCase,
  useSaveInvestorProfile,
  useSendCaseLoginDetails,
  useUnassignAdvisor,
  useUpdateCase,
  useUpdateCaseSeriousness,
  useUpdateCaseStatus,
} from '@/features/cases/hooks/use-case-writes'
import {
  useAddCaseGoal,
  useAddCaseHolding,
  useAddCasePrescriptionStep,
  useAddCaseTrade,
  useAddGoalFund,
  useDialCall,
  useEditCasePrescriptionStep,
  useRemoveCaseGoal,
  useRemoveCaseHolding,
  useRemoveCasePrescriptionStep,
  useRemoveCaseTrade,
  useRemoveGoalFund,
  useReorderCasePrescriptionStep,
  useReplaceCaseHoldings,
  useReplaceCaseTrades,
  useSetCaseGoalPlanApproved,
  useToggleCasePrescriptionStepDone,
  useUpdateCallOutcome,
  useUpdateCaseGoal,
  useUpdateCaseHolding,
  useUpdateCaseTrade,
  useUpdateGoalFund,
  useUpsertCaseBuildProfile,
} from '@/features/cases/hooks/use-case-subresource-writes'
import {
  useCaseDocumentDownload,
  useCaseDocumentPassword,
  useDeleteCaseDocument,
  useUploadCaseDocument,
} from '@/features/cases/hooks/use-case-document-writes'
import {
  caseDetailsSchema,
  createCaseSchema,
  logCaseCallSchema,
  saveInvestorProfileSchema,
  type CaseDetailsFormValues,
  type CreateCaseFormValues,
  type LogCaseCallFormValues,
  type SaveInvestorProfileFormValues,
} from '@/features/cases/schemas/case-forms.schema'
import { AppError } from '@/lib/errors/appError'
import { formatDate, formatDateTime } from '@/lib/format/date'
import {
  CALL_OUTCOME_LABEL,
  callOutcomeLabel,
  CASE_STATUS_LABEL,
  CASE_STATUS_PIPELINE,
  LEAD_STATUS_LABEL,
  SERVICE_TYPE_LABEL,
  type CallOutcome,
  type CallSummary,
  type CaseDetail,
  type CaseDocument,
  type CaseDocumentSummary,
  type CaseStatus,
  type DocumentKind,
  type SeriousnessStatus,
} from '@/features/cases/types'
import { DetailRow } from '@/features/cases/components/detail-row'
import {
  toMockCaseRecord,
  toMockHoldings,
  toMockSteps,
  toMockTrades,
} from '@/features/cases/lib/to-mock-shapes'
import {
  useInvestorCasesForStaff,
  useInvestorDetail,
  useInvestorReveal,
  useUpdateInvestorDetail,
  updateInvestorSchema,
  type InvestorCase,
  type InvestorDetail,
  type UpdateInvestorFormValues,
} from '@/features/investors'
import {
  createRecommendationSchema,
  useCreateRecommendation,
  useInvestorRecommendations,
  useUpdateRecommendationStatus,
  type CreateRecommendationFormValues,
  type RecommendationStatus,
} from '@/features/recommendations'
import { queryKeys } from '@/lib/queryKeys'

const DOCUMENT_KIND_MAP: Record<string, DocumentKind> = {
  cas_pdf: 'cas',
  broker_xlsx: 'tradebook',
  advisor_report: 'report',
}

const RECOMMENDATION_STATUS_LABEL: Record<RecommendationStatus, string> = {
  open: 'Open',
  accepted: 'Accepted',
  dismissed: 'Dismissed',
}

const RECOMMENDATION_STATUS_VARIANT: Record<
  RecommendationStatus,
  'outline' | 'default' | 'secondary'
> = {
  open: 'outline',
  accepted: 'default',
  dismissed: 'secondary',
}

export function toMockDocuments(items: CaseDocumentSummary[], caseId: string): CaseDocument[] {
  return items.map((d) => ({
    id: d.id,
    caseId,
    kind: DOCUMENT_KIND_MAP[d.kind] ?? 'report',
    fileName: d.fileName,
    sizeKb: Math.max(1, Math.round(d.sizeBytes / 1024)),
    uploadedAt: d.createdAt,
    uploadedBy: d.uploadedBy,
    // No per-document status field exists backend-side yet (Phase 7 wires real upload/parsing) —
    // 'parsed' reads as "already on file", not a pending/failed state, which is the honest default
    // for a document the backend already has.
    status: 'parsed',
  }))
}

/** `Math.round`, not `Math.floor` — a 90-second call reads as "2 min", matching how a human would
 * round a stopwatch reading. Exported for a lock-down unit test since it's the one piece of
 * display logic in the Calls tab most likely to silently drift. */
export function callDurationMinutes(durationSec: number): number {
  return Math.round(durationSec / 60)
}

function initialsOf(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/** Mirrors `cases.service.ts`'s `LEGAL_NEXT_STATUS` — duplicated deliberately (no shared package
 * exports this yet). `audit_ready` has no entry: its only legal next step is `POST
 * .../publish` (Phase 5), not this `PATCH .../status` endpoint, so it is never the "legal next"
 * step below and the last timeline circle stays permanently unreachable through this component
 * until Phase 5 wires publish. Exported for a small unit test locking it to the backend's map. */
export const CASE_STATUS_LEGAL_NEXT: Partial<Record<CaseStatus, CaseStatus>> = {
  submitted: 'consultation_scheduled',
  consultation_scheduled: 'consultation_completed',
  consultation_completed: 'audit_ready',
}

/** Mirrors the exact 5 checks `cases.service.ts`'s `publish` method runs, in the same order, so
 * the tooltip's wording lines up with what the backend would actually reject with. Exported for a
 * unit test locking it against silent drift, the same way `CASE_STATUS_LEGAL_NEXT` is. */
export function publishBlockers(caseDetail: CaseDetail): string[] {
  const blockers: string[] = []
  if (caseDetail.caseStatus !== 'audit_ready') blockers.push('must be Audit Ready')
  if (caseDetail.seriousnessStatus !== 'qualified') blockers.push('must be Qualified')
  if (!caseDetail.investor?.pan || !caseDetail.investor?.name || !caseDetail.investor?.email) {
    blockers.push('needs a complete investor profile (Save Profile)')
  }
  // Build Portfolio's deliverable is goals + a fund/SIP plan, not holdings/trades/prescription
  // steps (which that service type never populates) — mirrors the backend's own `publish()` gate.
  if (caseDetail.serviceType === 'build_portfolio') {
    if (caseDetail.counts.goals === 0) {
      blockers.push('needs at least one goal')
    }
  } else {
    if (caseDetail.counts.holdings === 0 && caseDetail.counts.trades === 0) {
      blockers.push('needs at least one holding or trade')
    }
    if (caseDetail.counts.prescriptionSteps === 0) {
      blockers.push('needs at least one prescription step')
    }
  }
  return blockers
}

/**
 * Compact, clickable version of the investor-facing pipeline — graphical dots
 * only, with one status line underneath instead of a label under every pill,
 * since a per-step label was most of this component's width. Only the single
 * legal next status (per `CASE_STATUS_LEGAL_NEXT`) is ever clickable — every
 * other circle, including a not-yet-legal future step, stays inert rather
 * than being clickable and 409ing (frontend-code-standards.md §12).
 * Publishing asks for confirmation first, but is unreachable through this
 * gate until Phase 5 wires `POST .../publish`.
 */
/** Exported for a direct unit test — same reasoning as `publishBlockers`/`CASE_STATUS_LEGAL_NEXT`
 * above: testing the confirm-before-advance behavior in isolation, without mounting the whole
 * `ClientDetail` tree and its many data hooks. */
export function StatusTimeline({
  status,
  onSelect,
  canPublish,
  publishBlockerList,
  onPublish,
  publishing,
  readOnly = false,
}: {
  status: CaseStatus
  onSelect: (status: CaseStatus) => void
  canPublish: boolean
  publishBlockerList: string[]
  onPublish: () => void
  publishing: boolean
  /** Admin views the pipeline but never advances it — only staff owns case-status changes. */
  readOnly?: boolean
}) {
  const currentIndex = CASE_STATUS_PIPELINE.indexOf(status)
  const lastIndex = CASE_STATUS_PIPELINE.length - 1
  const legalNextStep = CASE_STATUS_LEGAL_NEXT[status]
  const [pendingPublish, setPendingPublish] = useState(false)
  // Every non-last clickable step now confirms too — staff can't go back to an earlier status
  // once advanced, so a misclick here was previously unrecoverable without support intervention.
  const [pendingStatus, setPendingStatus] = useState<CaseStatus | null>(null)

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 items-center">
        {CASE_STATUS_PIPELINE.map((step, i) => {
          const isLast = i === lastIndex
          // Visual "act here next" state — independent of `readOnly`, so admin's read-only view
          // still shows the same highlight staff sees, just without the click affordance.
          const isNextUp = step === legalNextStep || (isLast && canPublish)
          const clickable = !readOnly && isNextUp
          // The current step reads as complete (checkmark) immediately, not just "up next" —
          // per direct feedback, clicking a status should visibly finish it right away rather
          // than waiting for the *next* status change to fill it in. This also folds in what used
          // to be a separate last-step special case (Published has no "next" step to mark it done
          // either way).
          const done = i <= currentIndex
          const tooltipText =
            isLast && !done
              ? canPublish
                ? 'Publish this case'
                : `Not ready: ${publishBlockerList.join(', ')}`
              : CASE_STATUS_LABEL[step]
          return (
            <div key={step} className="flex min-w-0 flex-1 items-center last:flex-none">
              <Tooltip>
                {/* Wrapped in a span, not applied to the button directly — a disabled button
                    doesn't reliably fire hover events in every browser, so the trigger needs a
                    non-disabled element around it (the standard Radix pattern for a disabled
                    trigger). This matters most here: the disabled Publish step is exactly the one
                    case where the tooltip (naming what's still missing) is most useful. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <button
                      type="button"
                      disabled={!clickable}
                      onClick={() => (isLast ? setPendingPublish(true) : setPendingStatus(step))}
                      aria-label={CASE_STATUS_LABEL[step]}
                      className={cn(
                        'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-4 transition-all',
                        !clickable && 'cursor-default',
                        done &&
                          'from-primary text-primary-foreground ring-primary/10 bg-gradient-to-br to-indigo-500 shadow-sm',
                        // Up-next (the one legal next step, or Publish once eligible) gets its own
                        // highlight so it reads as "act here next", not just another greyed-out
                        // future step — distinct from `done`'s filled checkmark style. Same ring
                        // treatment the old "current" state used before immediate-fill replaced
                        // it. Shown for admin's read-only view too (`isNextUp`, not `clickable`).
                        !done &&
                          isNextUp &&
                          'bg-card text-primary ring-primary/25 shadow-[0_0_0_2px_var(--primary)]',
                        !done && !isNextUp && 'bg-muted text-muted-foreground ring-transparent',
                        !done && clickable && 'hover:ring-border hover:scale-105',
                      )}
                    >
                      {done ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
                    </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
              {!isLast && (
                <span
                  aria-hidden="true"
                  className="bg-border relative h-1 min-w-4 flex-1 overflow-hidden rounded-full"
                >
                  {done && (
                    <span className="from-primary absolute inset-0 rounded-full bg-gradient-to-r to-indigo-500" />
                  )}
                </span>
              )}
            </div>
          )
        })}
      </div>
      <p className="text-xs sm:text-sm">
        <span className="text-muted-foreground">
          Step {currentIndex + 1} of {CASE_STATUS_PIPELINE.length} —
        </span>{' '}
        <span className="text-primary font-semibold">{CASE_STATUS_LABEL[status]}</span>
      </p>

      <AlertDialog open={!!pendingStatus} onOpenChange={(open) => !open && setPendingStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Advance to {pendingStatus ? CASE_STATUS_LABEL[pendingStatus] : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This moves the case forward in the pipeline. You can&apos;t go back to an earlier
              status from here once confirmed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingStatus) onSelect(pendingStatus)
                setPendingStatus(null)
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={pendingPublish} onOpenChange={setPendingPublish}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish this case?</DialogTitle>
            <DialogDescription>
              The investor will immediately see this review on their dashboard. This can&apos;t be
              undone from here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              disabled={publishing}
              onClick={() => {
                onPublish()
                setPendingPublish(false)
              }}
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** `ringing` means staff already answered and Plivo is now dialing the investor — distinct from
 * `initiating` (staff's own phone hasn't rung yet). `in_progress` only ever arrives via the
 * `dial-progress` webhook's `DialAction=answer` event, fired the instant the investor's leg picks
 * up — see `plivo-webhook.service.ts`'s `handleDialProgress`. */
function dialStatusMessage(status: string, investorName: string | null): string {
  if (status === 'initiating') return 'Calling you now — pick up to connect…'
  if (status === 'ringing') {
    return investorName ? `Connecting you to ${investorName}…` : 'Connecting the call…'
  }
  if (status === 'in_progress') return 'Connected — recording.'
  return 'Connecting…'
}

/** Click-to-call — the real Plivo entry point. Polls `useCallStatus` from the moment `dial`
 * succeeds until the call reaches a terminal status, then hands off to `WrapUpCallForm` for
 * outcome + notes — the same `CallLog` row, never a second one. */
function DialCallPanel({
  caseId,
  investorName,
  phone,
}: {
  caseId: string
  investorName: string | null
  phone: string | null
}) {
  const dial = useDialCall(caseId)
  const [activeCallId, setActiveCallId] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const statusQuery = useCallStatus(activeCallId ?? undefined, { enabled: !!activeCallId })
  const status = statusQuery.data?.status

  const onDial = () => {
    setConfirmOpen(false)
    dial.mutate(undefined, { onSuccess: (result) => setActiveCallId(result.id) })
  }

  const onWrappedUp = () => setActiveCallId(null)

  if (activeCallId && status && status !== 'completed' && status !== 'failed') {
    return (
      <div className="flex items-center gap-2 rounded-lg border p-3 text-sm">
        <Loader2 className="text-muted-foreground size-4 animate-spin" />
        <span>{dialStatusMessage(status, investorName)}</span>
      </div>
    )
  }

  if (activeCallId && status === 'failed') {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border p-3 text-sm">
        <span className="text-muted-foreground">Call did not connect.</span>
        <Button type="button" size="sm" variant="outline" onClick={onWrappedUp}>
          Dismiss
        </Button>
      </div>
    )
  }

  if (activeCallId && status === 'completed' && statusQuery.data) {
    if (statusQuery.data.outcome === null) {
      return <WrapUpCallForm call={statusQuery.data} onDone={onWrappedUp} />
    }
    // Already wrapped up (e.g. a second browser tab beat this one to it) — nothing more to show.
    onWrappedUp()
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={dial.isPending || !phone}
      >
        <PhoneCall className="size-4" />
        {dial.isPending ? 'Calling…' : `Call ${investorName ?? 'investor'}`}
      </Button>
      {!phone && (
        <p className="text-muted-foreground text-xs">
          Set your mobile number in Profile to use this.
        </p>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Call {investorName ?? 'this investor'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will place a real, recorded phone call to {investorName ?? 'the investor'} right
              away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDial} disabled={dial.isPending}>
              {dial.isPending ? 'Calling…' : 'Call now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** The wrap-up submission for a real Plivo call — outcome + notes only, no duration field (the
 * real duration already came from the hangup webhook). Updates the same `CallLog` row via
 * `useUpdateCallOutcome`, never creating a second one. */
function WrapUpCallForm({ call, onDone }: { call: CallSummary; onDone: () => void }) {
  const updateOutcome = useUpdateCallOutcome(call.caseId)
  const form = useForm<z.input<typeof logCaseCallSchema>, unknown, LogCaseCallFormValues>({
    resolver: zodResolver(logCaseCallSchema),
    mode: 'onTouched',
    defaultValues: { durationMin: 0, outcome: 'connected', notes: '', followUpAt: '' },
  })
  const outcome = form.watch('outcome')

  const onSubmit = form.handleSubmit(async (values) => {
    const followUpAt =
      values.outcome === 'follow_up_scheduled' && values.followUpAt
        ? new Date(values.followUpAt).toISOString()
        : undefined
    await updateOutcome.mutateAsync({
      callId: call.id,
      input: { outcome: values.outcome, notes: values.notes || undefined, followUpAt },
    })
    toast({
      variant: 'success',
      title: followUpAt
        ? `Call wrapped up — follow-up set for ${values.followUpAt}`
        : 'Call wrapped up',
    })
    onDone()
  })

  return (
    <form onSubmit={onSubmit} className="rounded-lg border p-3">
      <FieldGroup>
        <p className="text-sm font-semibold">Wrap up this call</p>
        <p className="text-muted-foreground text-xs">
          Connected for {callDurationMinutes(call.durationSec)} min. Add the outcome and notes.
        </p>
        <Field>
          <FieldLabel htmlFor="wrapup-outcome">Outcome</FieldLabel>
          <Select
            value={form.watch('outcome')}
            onValueChange={(v) => form.setValue('outcome', v as CallOutcome)}
          >
            <SelectTrigger id="wrapup-outcome" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CALL_OUTCOME_LABEL).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {outcome === 'follow_up_scheduled' && (
          <Field>
            <FieldLabel htmlFor="wrapup-follow-up">Follow-up date</FieldLabel>
            <Input id="wrapup-follow-up" type="date" {...form.register('followUpAt')} />
            <FieldError errors={[form.formState.errors.followUpAt]} />
          </Field>
        )}
        <Field>
          <FieldLabel htmlFor="wrapup-notes">
            Notes{outcome !== 'no_answer' && ' (required)'}
          </FieldLabel>
          <Textarea
            id="wrapup-notes"
            rows={3}
            placeholder="What did you cover on this call?"
            {...form.register('notes')}
          />
          <FieldError errors={[form.formState.errors.notes]} />
        </Field>
        <Button type="submit" className="w-fit self-start" disabled={updateOutcome.isPending}>
          {updateOutcome.isPending ? 'Saving…' : 'Save'}
        </Button>
      </FieldGroup>
    </form>
  )
}

function CallsList({ calls, caseId }: { calls: CallSummary[]; caseId: string }) {
  const [selectedCall, setSelectedCall] = useState<CallSummary | null>(null)

  return (
    <>
      <CallLogList
        calls={calls}
        emptyMessage="No calls with this client yet."
        onSelect={setSelectedCall}
      />

      {selectedCall && (
        <CallDetailDialog
          call={selectedCall}
          caseId={caseId}
          open={!!selectedCall}
          onOpenChange={(open) => !open && setSelectedCall(null)}
        />
      )}
    </>
  )
}

function CallLogList({
  calls,
  emptyMessage,
  onSelect,
}: {
  calls: CallSummary[]
  emptyMessage: string
  onSelect: (call: CallSummary) => void
}) {
  if (calls.length === 0) {
    return <p className="text-muted-foreground text-sm">{emptyMessage}</p>
  }
  return (
    <div className="flex flex-col gap-2">
      {calls.map((log) => (
        <button
          key={log.id}
          type="button"
          onClick={() => onSelect(log)}
          className="hover:bg-accent/50 rounded-lg border p-3 text-left transition-colors"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Badge variant="outline">{callOutcomeLabel(log.outcome)}</Badge>
            <span className="text-muted-foreground text-xs">
              {formatDateTime(log.startedAt)} · {callDurationMinutes(log.durationSec)} min
            </span>
          </div>
          {log.notes && (
            <p className="text-muted-foreground mt-2 line-clamp-2 text-sm">{log.notes}</p>
          )}
          {log.hasRecording && (
            <span className="text-muted-foreground mt-2 inline-flex items-center gap-1.5 text-xs">
              <Mic className="size-3.5" /> Recording — click to play
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

/** Note / next-follow-up / wealth-score — the three `PATCH /cases/:id` fields (`UpdateCaseDto`),
 * edited together since they share one endpoint. Submits all three every time rather than
 * diffing dirty fields, so the "at least one field" backend rule is trivially satisfied. Clearing
 * the note to blank does not clear it server-side (an empty string is dropped the same as an
 * untouched field) — there is no dedicated "clear note" affordance this phase. */
function CaseDetailsDialog({
  caseDetail,
  open,
  onOpenChange,
  mutation,
}: {
  caseDetail: CaseDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  mutation: ReturnType<typeof useUpdateCase>
}) {
  const form = useForm<z.input<typeof caseDetailsSchema>, unknown, CaseDetailsFormValues>({
    resolver: zodResolver(caseDetailsSchema),
    mode: 'onTouched',
    values: {
      note: caseDetail.note ?? '',
      nextFollowUpAt: caseDetail.nextFollowUpAt ? caseDetail.nextFollowUpAt.slice(0, 10) : '',
      wealthScore: caseDetail.wealthScore ?? undefined,
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync({
      note: values.note || undefined,
      nextFollowUpAt: values.nextFollowUpAt
        ? new Date(values.nextFollowUpAt).toISOString()
        : undefined,
      wealthScore: values.wealthScore,
    })
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit case details</DialogTitle>
          <DialogDescription>Note, follow-up date, and wealth score.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="case-note">Note</FieldLabel>
              <Textarea id="case-note" rows={4} {...form.register('note')} />
              <FieldError errors={[form.formState.errors.note]} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="case-next-follow-up">Next follow-up</FieldLabel>
                <Input id="case-next-follow-up" type="date" {...form.register('nextFollowUpAt')} />
                <FieldError errors={[form.formState.errors.nextFollowUpAt]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="case-wealth-score">Wealth score</FieldLabel>
                <Input
                  id="case-wealth-score"
                  type="number"
                  min={0}
                  max={100}
                  {...form.register('wealthScore')}
                />
                <FieldError errors={[form.formState.errors.wealthScore]} />
              </Field>
            </div>
          </FieldGroup>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Mirrors `SaveInvestorProfileDto` — this endpoint is fully idempotent (re-upserts every field
 * and reuses an existing Clerk account by email each time), so this dialog is always enabled for
 * staff, not gated on "already saved"; it doubles as the correction path until Phase 9's dedicated
 * `PATCH /investors/:id` exists. */
function SaveInvestorProfileDialog({
  caseDetail,
  open,
  onOpenChange,
}: {
  caseDetail: CaseDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const mutation = useSaveInvestorProfile(caseDetail.id)
  // `caseDetail.investor.mobile`/`.pan` are masked for staff — reveal the real values while this
  // dialog is open so a re-save can't silently overwrite them with the masked placeholder string.
  const reveal = useInvestorReveal(caseDetail.investor?.id, { enabled: open })
  const form = useForm<
    z.input<typeof saveInvestorProfileSchema>,
    unknown,
    SaveInvestorProfileFormValues
  >({
    resolver: zodResolver(saveInvestorProfileSchema),
    mode: 'onTouched',
    values: {
      name: caseDetail.investor?.name ?? '',
      email: caseDetail.investor?.email ?? '',
      mobile: reveal.data?.mobile ?? caseDetail.investor?.mobile ?? caseDetail.contactMobile,
      pan: reveal.data?.pan ?? caseDetail.investor?.pan ?? '',
      city: caseDetail.investor?.city ?? '',
      age: caseDetail.investor?.age ?? '',
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save investor profile</DialogTitle>
          <DialogDescription>
            Creates or updates this investor&apos;s login-capable account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="profile-name">Full name</FieldLabel>
              <Input id="profile-name" {...form.register('name')} />
              <FieldError errors={[form.formState.errors.name]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="profile-email">Email</FieldLabel>
              <Input id="profile-email" type="email" {...form.register('email')} />
              <FieldError errors={[form.formState.errors.email]} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="profile-mobile">Mobile</FieldLabel>
                <Input
                  id="profile-mobile"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  {...form.register('mobile', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    },
                  })}
                />
                <FieldError errors={[form.formState.errors.mobile]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="profile-pan">PAN</FieldLabel>
                <Input
                  id="profile-pan"
                  maxLength={10}
                  {...form.register('pan', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
                    },
                  })}
                />
                <FieldError errors={[form.formState.errors.pan]} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="profile-city">City</FieldLabel>
                <Input id="profile-city" {...form.register('city')} />
              </Field>
              <Field>
                <FieldLabel htmlFor="profile-age">Age</FieldLabel>
                <Input
                  id="profile-age"
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 38"
                  {...form.register('age')}
                />
                <FieldError errors={[form.formState.errors.age]} />
              </Field>
            </div>
          </FieldGroup>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Post-account-creation counterpart to `SaveInvestorProfileDialog` — once the investor has a login
 * account (credentials sent via ANY of their cases, not just this one — see `credentialsAlreadySent`
 * in the parent), the Pencil opens this instead: a plain `PATCH /investors/:id` with no Clerk side
 * effects, all fields optional. Submits the whole form rather
 * than diffing dirty fields, matching `SaveInvestorProfileDialog`'s own "send everything" pattern
 * above — every field is optional on the DTO, so re-sending an unchanged value is harmless. */
function EditInvestorDetailsDialog({
  investorId,
  investor,
  open,
  onOpenChange,
}: {
  investorId: string
  investor: CaseDetail['investor']
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const mutation = useUpdateInvestorDetail(investorId)
  // `investor.mobile`/`.pan` are masked for staff — reveal the real values while this dialog is
  // open so a save can't silently overwrite them with the masked placeholder string.
  const reveal = useInvestorReveal(investorId, { enabled: open })
  const form = useForm<z.input<typeof updateInvestorSchema>, unknown, UpdateInvestorFormValues>({
    resolver: zodResolver(updateInvestorSchema),
    mode: 'onTouched',
    values: {
      name: investor?.name ?? '',
      email: investor?.email ?? '',
      mobile: reveal.data?.mobile ?? investor?.mobile ?? '',
      pan: reveal.data?.pan ?? investor?.pan ?? '',
      city: investor?.city ?? '',
      age: investor?.age ?? '',
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit investor details</DialogTitle>
          <DialogDescription>
            Updates this investor&apos;s profile — no login impact.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="edit-investor-name">Full name</FieldLabel>
              <Input id="edit-investor-name" {...form.register('name')} />
              <FieldError errors={[form.formState.errors.name]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="edit-investor-email">Email</FieldLabel>
              <Input id="edit-investor-email" type="email" {...form.register('email')} />
              <FieldError errors={[form.formState.errors.email]} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="edit-investor-mobile">Mobile</FieldLabel>
                <Input
                  id="edit-investor-mobile"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  {...form.register('mobile', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    },
                  })}
                />
                <FieldError errors={[form.formState.errors.mobile]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="edit-investor-pan">PAN</FieldLabel>
                <Input
                  id="edit-investor-pan"
                  maxLength={10}
                  {...form.register('pan', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
                    },
                  })}
                />
                <FieldError errors={[form.formState.errors.pan]} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="edit-investor-city">City</FieldLabel>
                <Input id="edit-investor-city" {...form.register('city')} />
              </Field>
              <Field>
                <FieldLabel htmlFor="edit-investor-age">Age</FieldLabel>
                <Input
                  id="edit-investor-age"
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 38"
                  {...form.register('age')}
                />
                <FieldError errors={[form.formState.errors.age]} />
              </Field>
            </div>
          </FieldGroup>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Mirrors `CreateRecommendationDto` — a plain create, no confirm step. `build_portfolio` is a
 * legal option here (unlike `createCaseSchema`'s service-type enum) since recommending the one
 * service an investor doesn't have yet is the point. */
function RecommendDialog({
  open,
  onOpenChange,
  mutation,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mutation: ReturnType<typeof useCreateRecommendation>
}) {
  const form = useForm<
    z.input<typeof createRecommendationSchema>,
    unknown,
    CreateRecommendationFormValues
  >({
    resolver: zodResolver(createRecommendationSchema),
    mode: 'onTouched',
    defaultValues: { serviceType: 'portfolio_audit', reason: '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
    onOpenChange(false)
    form.reset()
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recommend a service</DialogTitle>
          <DialogDescription>Suggest a next service for this investor.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="recommend-service">Service</FieldLabel>
              <Select
                value={form.watch('serviceType')}
                onValueChange={(v) =>
                  form.setValue('serviceType', v as CreateRecommendationFormValues['serviceType'])
                }
              >
                <SelectTrigger id="recommend-service" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="portfolio_audit">
                    {SERVICE_TYPE_LABEL.portfolio_audit}
                  </SelectItem>
                  <SelectItem value="trades_audit">{SERVICE_TYPE_LABEL.trades_audit}</SelectItem>
                  <SelectItem value="build_portfolio">
                    {SERVICE_TYPE_LABEL.build_portfolio}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="recommend-reason">Reason</FieldLabel>
              <Textarea
                id="recommend-reason"
                rows={3}
                placeholder="Why are you recommending this?"
                {...form.register('reason')}
              />
              <FieldError errors={[form.formState.errors.reason]} />
            </Field>
          </FieldGroup>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Sending…' : 'Recommend'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const CASE_LESS_CREATE_DEFAULTS: CreateCaseFormValues = {
  serviceType: 'portfolio_audit',
  name: '',
  email: '',
  mobile: '',
  pan: undefined,
  city: '',
  seriousnessStatus: 'pending',
}

/** `AddLeadDialog` (`staff-clients-view.tsx`) is a private, unexported, prop-less component — it
 * takes no prefill input and editing it is out of scope for this file, so rather than reusing it
 * directly this is a small local dialog built on the same `createCaseSchema`/`useCreateCase()` it
 * uses, just prefilled from the case-less investor's existing identity fields. */
function CreateFirstCaseDialog({
  investor,
  open,
  onOpenChange,
  onCreated,
}: {
  investor: InvestorDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (createdCaseId: string) => void
}) {
  const createCase = useCreateCase()
  const form = useForm<z.input<typeof createCaseSchema>, unknown, CreateCaseFormValues>({
    resolver: zodResolver(createCaseSchema),
    mode: 'onTouched',
    values: {
      ...CASE_LESS_CREATE_DEFAULTS,
      name: investor.name ?? '',
      email: investor.email ?? '',
      mobile: investor.mobile,
      pan: investor.pan ?? undefined,
      city: investor.city ?? '',
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    const created = await createCase.mutateAsync({
      ...values,
      pan: values.pan || undefined,
      city: values.city || undefined,
    })
    onOpenChange(false)
    onCreated(created.id)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a case for this investor</DialogTitle>
          <DialogDescription>
            Same investor (matched by PAN/mobile) — this just adds their first case.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="first-case-service">Service</FieldLabel>
              <Select
                value={form.watch('serviceType')}
                onValueChange={(v) =>
                  form.setValue('serviceType', v as CreateCaseFormValues['serviceType'])
                }
              >
                <SelectTrigger id="first-case-service" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="portfolio_audit">
                    {SERVICE_TYPE_LABEL.portfolio_audit}
                  </SelectItem>
                  <SelectItem value="trades_audit">{SERVICE_TYPE_LABEL.trades_audit}</SelectItem>
                  <SelectItem value="build_portfolio">
                    {SERVICE_TYPE_LABEL.build_portfolio}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="first-case-name">Full name</FieldLabel>
              <Input id="first-case-name" {...form.register('name')} />
              <FieldError errors={[form.formState.errors.name]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="first-case-email">Email</FieldLabel>
              <Input id="first-case-email" type="email" {...form.register('email')} />
              <FieldError errors={[form.formState.errors.email]} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="first-case-mobile">Mobile</FieldLabel>
                <Input
                  id="first-case-mobile"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  {...form.register('mobile', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    },
                  })}
                />
                <FieldError errors={[form.formState.errors.mobile]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="first-case-pan">PAN (optional)</FieldLabel>
                <Input
                  id="first-case-pan"
                  maxLength={10}
                  {...form.register('pan', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
                    },
                  })}
                />
                <FieldError errors={[form.formState.errors.pan]} />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="first-case-city">City</FieldLabel>
              <Input id="first-case-city" {...form.register('city')} />
            </Field>
          </FieldGroup>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createCase.isPending}>
              {createCase.isPending ? 'Creating…' : 'Create case'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Case-less investor state: an investor row exists (created via a prior `POST /cases` that
 * matched their PAN/mobile, then that case was since removed, or some other edge) but has zero
 * cases right now. Read-only identity — no edit affordance here on purpose: `PATCH
 * /investors/:investorId` is staff-only, but staff can never actually reach a case-less investor's
 * detail page in normal navigation (a staff member's own client list is built entirely from cases
 * they're assigned to), so there is genuinely no UI path to edit a case-less investor's details by
 * design of the backend's own RBAC — the correction path is either re-running Add Investor (which
 * upserts by PAN/mobile) or just creating their first case below, after which the normal
 * profile-edit surfaces above apply. */
function CaseLessInvestorCard({
  investorId,
  mode,
  backTo,
}: {
  investorId: string
  mode: 'staff' | 'admin'
  backTo: Route
}) {
  const router = useRouter()
  const investorQuery = useInvestorDetail(investorId)
  const [creating, setCreating] = useState(false)

  if (investorQuery.isLoading) {
    return <Skeleton className="h-40 w-full" />
  }

  if (investorQuery.isError || !investorQuery.data) {
    return (
      <QueryErrorState error={investorQuery.error} onRetry={() => void investorQuery.refetch()} />
    )
  }

  const investor = investorQuery.data

  return (
    <div className="flex flex-col gap-4">
      <Button variant="ghost" size="sm" className="-ml-2 w-fit" onClick={() => router.push(backTo)}>
        <ArrowLeft className="size-4" /> Back
      </Button>
      <Empty className="border border-dashed py-8">
        <EmptyHeader>
          <EmptyTitle>No case yet</EmptyTitle>
          <EmptyDescription>This investor exists but has no case on file yet.</EmptyDescription>
        </EmptyHeader>
      </Empty>
      <div className="flex flex-col gap-2 rounded-lg border p-4">
        <DetailRow label="Name" value={investor.name || '—'} />
        <DetailRow label="Email" value={investor.email || '—'} />
        <DetailRow label="Mobile" value={investor.mobile || '—'} />
        <DetailRow label="PAN" value={investor.pan || '—'} />
        <DetailRow label="City" value={investor.city || '—'} />
      </div>
      <Button className="w-fit" onClick={() => setCreating(true)}>
        Create a case for this investor
      </Button>
      <CreateFirstCaseDialog
        investor={investor}
        open={creating}
        onOpenChange={setCreating}
        onCreated={(createdCaseId) => {
          const prefix = mode === 'admin' ? '/admin/clients' : '/staff/clients'
          router.push(`${prefix}/${investorId}?case=${createdCaseId}`)
        }}
      />
    </div>
  )
}

/**
 * The client, not the case. A person accumulates services over time and a
 * call covers whatever they raise, so the durable object here is the
 * investor — their cases hang off them as panels. `mode` is the only
 * staff/admin difference: admin reads, staff acts. Case-status changes and
 * advisor assignment are ops decisions handled elsewhere (the status
 * timeline is staff-only here; assignment lives in the admin Cases table).
 *
 * "Preview client dashboard" is available to both staff and admin (admin read-only, same as the
 * rest of this view) and unlocks once the case reaches Audit Ready — it renders the real
 * investor-dashboard Home content via `ClientDashboardPreview`, resolved by investor id rather
 * than the real investor's own session.
 */
export function ClientDetail({
  investorId,
  focusCaseId,
  mode,
  backTo,
}: {
  investorId: string
  focusCaseId?: string | null
  mode: 'staff' | 'admin'
  backTo: Route
}) {
  const router = useRouter()
  const isStaff = mode === 'staff'
  const pathPrefix = mode === 'admin' ? '/admin/clients' : '/staff/clients'
  const caseQuery = useCaseDetail(focusCaseId ?? undefined)
  const caseDetail = caseQuery.data
  // Real as of Phase 9 (`GET /investors/:id/cases`) — fetched unconditionally so both the
  // case-switcher strip and the "no case yet" / auto-redirect logic below can use it.
  const investorCasesQuery = useInvestorCasesForStaff(investorId)
  const investorCases = useMemo(() => investorCasesQuery.data ?? [], [investorCasesQuery.data])
  const holdingsQuery = useCaseHoldings(caseDetail?.id)
  const tradesQuery = useCaseTrades(caseDetail?.id, 1, 50)
  const stepsQuery = useCasePrescriptionSteps(caseDetail?.id)
  const documentsQuery = useCaseDocumentList(caseDetail?.id)
  const goalsQuery = useCaseGoals(caseDetail?.id)
  // Scoped via search=<case reference> — case references are globally unique, and GET /calls has
  // no direct case/investor filter, so this reliably scopes to just this one case's calls without
  // any backend change (confirmed via AskUserQuestion).
  const callsQuery = useCalls(
    { search: caseDetail?.reference, pageSize: 50 },
    { enabled: !!caseDetail },
  )
  const myCalls = callsQuery.data?.items ?? []
  // Gates the Call button on the staff member's `phone` being set — fetched unconditionally
  // (cheap, already cached app-wide from the dashboard shell) rather than only within the panel.
  const meQuery = useMe()
  const updateStatus = useUpdateCaseStatus(caseDetail?.id ?? '')
  const updateCaseDetails = useUpdateCase(caseDetail?.id ?? '')
  const publishCase = usePublishCase(caseDetail?.id ?? '')
  const updateSeriousness = useUpdateCaseSeriousness()
  const assignAdvisor = useAssignAdvisor()
  const unassignAdvisor = useUnassignAdvisor()
  const claimCase = useClaimCase()
  // Admin-only roster for the inline advisor combobox — `GET /admin/users` 403s for staff, so this
  // stays disabled outside admin mode (mirrors `admin-cases-view.tsx`'s own `pageSize: 100` roster
  // fetch, the same known "caps at 100 staff" limitation tracked in api-integration-progress.md).
  const { data: staffPage } = useAdminUsers({ pageSize: 100 }, { enabled: !isStaff })
  const activeStaff = useMemo(
    () => (staffPage?.items ?? []).filter((s) => s.role === 'staff' && s.isActive),
    [staffPage],
  )
  const sendLoginDetails = useSendCaseLoginDetails(caseDetail?.id ?? '')
  const addHolding = useAddCaseHolding(caseDetail?.id ?? '')
  const removeHolding = useRemoveCaseHolding(caseDetail?.id ?? '')
  const updateHolding = useUpdateCaseHolding(caseDetail?.id ?? '')
  const replaceHoldings = useReplaceCaseHoldings(caseDetail?.id ?? '')
  const addTrade = useAddCaseTrade(caseDetail?.id ?? '')
  const removeTrade = useRemoveCaseTrade(caseDetail?.id ?? '')
  const updateTrade = useUpdateCaseTrade(caseDetail?.id ?? '')
  const replaceTrades = useReplaceCaseTrades(caseDetail?.id ?? '')
  const addStep = useAddCasePrescriptionStep(caseDetail?.id ?? '')
  const editStep = useEditCasePrescriptionStep(caseDetail?.id ?? '')
  const removeStep = useRemoveCasePrescriptionStep(caseDetail?.id ?? '')
  const reorderStep = useReorderCasePrescriptionStep(caseDetail?.id ?? '')
  const toggleStepDone = useToggleCasePrescriptionStepDone(caseDetail?.id ?? '')
  const addGoal = useAddCaseGoal(caseDetail?.id ?? '')
  const updateGoal = useUpdateCaseGoal(caseDetail?.id ?? '')
  const removeGoal = useRemoveCaseGoal(caseDetail?.id ?? '')
  const setGoalApproved = useSetCaseGoalPlanApproved(caseDetail?.id ?? '')
  const addGoalFund = useAddGoalFund(caseDetail?.id ?? '')
  const updateGoalFund = useUpdateGoalFund(caseDetail?.id ?? '')
  const removeGoalFund = useRemoveGoalFund(caseDetail?.id ?? '')
  const upsertBuildProfile = useUpsertCaseBuildProfile(caseDetail?.id ?? '')
  const uploadDocument = useUploadCaseDocument(caseDetail?.id ?? '')
  const deleteDocument = useDeleteCaseDocument(caseDetail?.id ?? '')
  const downloadDocument = useCaseDocumentDownload(caseDetail?.id ?? '')
  const getDocumentPassword = useCaseDocumentPassword(caseDetail?.id ?? '')
  // Visible to both staff (recommend + withdraw) and admin (read-only) — GET is staff+admin.
  const recommendationsQuery = useInvestorRecommendations(investorId)
  const createRecommendation = useCreateRecommendation(investorId)
  const updateRecommendationStatus = useUpdateRecommendationStatus(
    queryKeys.recommendations.byInvestor(investorId),
  )

  // Initialized from `focusCaseId`, not a hardcoded `'overview'` — `StaffClientDetailView`/
  // `AdminClientDetailView` remount this component (via a `key` on `focusCaseId`) whenever the
  // focused case changes, so a fresh instance sees the *new* `focusCaseId` immediately and lands
  // on the right tab in one click, instead of resetting to Overview and requiring a second click.
  const [activeSection, setActiveSection] = useState<string>(focusCaseId ?? 'overview')
  const [editingDetails, setEditingDetails] = useState(false)
  const [editingProfile, setEditingProfile] = useState(false)
  const [editingInvestorDetails, setEditingInvestorDetails] = useState(false)
  const [confirmingSendLogin, setConfirmingSendLogin] = useState(false)
  const [recommending, setRecommending] = useState(false)
  // Staged, not immediate — matches admin-cases-view.tsx's own advisor-assign safety net exactly,
  // so a misclick here can't instantly reassign a case either.
  const [pendingAdvisorAssign, setPendingAdvisorAssign] = useState<{
    staffId: string
    staffName: string
  } | null>(null)
  // Staged, same reasoning as `pendingAdvisorAssign` — a misclick on this button shouldn't
  // instantly claim a case either.
  const [confirmingClaim, setConfirmingClaim] = useState(false)

  // No `?case=` yet but the investor already has case(s) — land on the most recent one instead of
  // showing an empty state. `replace`, not `push`, so this doesn't add a back-button entry.
  useEffect(() => {
    if (focusCaseId || investorCasesQuery.isLoading || investorCases.length === 0) return
    const mostRecent = [...investorCases].sort((a, b) =>
      a.createdAt < b.createdAt ? 1 : -1,
    )[0] as InvestorCase
    router.replace(`${pathPrefix}/${investorId}?case=${mostRecent.id}`)
  }, [focusCaseId, investorCasesQuery.isLoading, investorCases, investorId, pathPrefix, router])

  if (!focusCaseId) {
    if (investorCasesQuery.isLoading) {
      return <Skeleton className="h-40 w-full" />
    }
    if (investorCases.length === 0) {
      return <CaseLessInvestorCard investorId={investorId} mode={mode} backTo={backTo} />
    }
    // Has case(s) but hasn't redirected yet (effect above fires on the next tick) — brief skeleton.
    return <Skeleton className="h-40 w-full" />
  }

  if (caseQuery.isLoading) {
    return <Skeleton className="h-40 w-full" />
  }

  if (caseQuery.isError) {
    return <QueryErrorState error={caseQuery.error} onRetry={() => void caseQuery.refetch()} />
  }

  if (!caseDetail) {
    return (
      <Empty className="border border-dashed py-12">
        <EmptyHeader>
          <EmptyTitle>Client not found</EmptyTitle>
          <EmptyDescription>This client may have been removed.</EmptyDescription>
        </EmptyHeader>
        <Button variant="outline" onClick={() => router.push(backTo)}>
          Back
        </Button>
      </Empty>
    )
  }

  const investor = caseDetail.investor
  // The backend's `assertCaseAccess`/`assertInvestorAccess` now let a staff viewer READ an
  // unclaimed case (so they can open it and decide whether to claim it), but every write endpoint
  // still requires real ownership (`assignedStaffId === me.id`) and 404s otherwise. Every edit
  // control on this page must mirror that split client-side too — `isStaff` alone is no longer
  // enough to show an edit affordance; it must also be claimed. Admin is unaffected (never gated on
  // `isClaimed` — admin's own writes are a separate, deliberately narrow surface elsewhere).
  const isClaimed = caseDetail.advisor !== null
  // Login credentials are sent once per INVESTOR, not once per case — `loginDetailsSentAt` lives on
  // the Case row (so each case can still show its own "last sent" timestamp), but whether to offer
  // "Send" vs "Resend" must look across every one of the investor's cases: if a sibling case already
  // sent credentials, this case's own button must say "Resend" too, since sending again here would
  // otherwise silently rotate (invalidate) a password the investor already has. Also checks
  // `caseDetail` directly (not just `investorCases`), which in production is redundant (the focused
  // case is always one of `investorCases` too) but keeps this correct even if the case-list query is
  // still loading when the case-detail query has already resolved.
  const credentialsAlreadySent =
    !!caseDetail.loginDetailsSentAt || investorCases.some((c) => c.loginDetailsSentAt)
  const mockCaseRecord = toMockCaseRecord(caseDetail)
  const subResourcesLoading =
    holdingsQuery.isLoading ||
    tradesQuery.isLoading ||
    stepsQuery.isLoading ||
    documentsQuery.isLoading ||
    goalsQuery.isLoading
  const subResourcesError =
    holdingsQuery.error ??
    tradesQuery.error ??
    stepsQuery.error ??
    documentsQuery.error ??
    goalsQuery.error

  // `assertCaseAccess` 404s a staff viewer for any case not assigned to them OR assigned to
  // someone else (even though `investorCases` here lists every case the investor has, across every
  // assigned staff member) — this predicts that 404 client-side so the tab/card is disabled instead
  // of navigating into a dead-end error. An UNassigned sibling case is the one exception: staff can
  // now read (though not edit) an unclaimed case to decide whether to claim it, so its tab stays
  // open rather than disabled. Admin has no such restriction, so this is always true in admin mode.
  const canOpenCase = (c: InvestorCase) =>
    !isStaff || c.advisorId === meQuery.data?.id || c.advisorId === null
  // Includes the owner's email, not just name — two staff can share a display name, and this is an
  // internal, staff-to-staff tooltip (never shown to an investor), so showing it is not a privacy
  // concern, same convention as other internal CRM/ops tools showing a record's real owner.
  const caseOwnerLabel = (c: InvestorCase) =>
    c.advisorName
      ? `Handled by ${c.advisorName}${c.advisorEmail ? ` (${c.advisorEmail})` : ''}`
      : 'Unassigned'

  // Replaces the old standalone case-switcher badge row — same navigation call
  // (`router.push(...?case=<id>)`), now triggered from the main tab bar instead of a separate
  // strip of badges. `investorCases.find` returns undefined for 'overview'/'calls', so those just
  // update local state with no navigation.
  const handleSectionChange = (value: string) => {
    setActiveSection(value)
    const targetCase = investorCases.find((c) => c.id === value)
    if (targetCase && targetCase.id !== focusCaseId) {
      router.push(`${pathPrefix}/${investorId}?case=${targetCase.id}`)
    }
  }

  const activity = myCalls.slice(0, 5)
  const blockers = publishBlockers(caseDetail)
  const canPreviewDashboard =
    caseDetail.caseStatus === 'audit_ready' || caseDetail.caseStatus === 'published'

  // Mirrors admin-cases-view.tsx's onAdvisorChange/confirmAssign exactly — unassign fires
  // immediately (lower-stakes, easily reversible), assign stages behind a confirm dialog.
  const onAdvisorChange = (value: string) => {
    if (value === ADVISOR_UNASSIGNED_VALUE) {
      unassignAdvisor.mutate(caseDetail.id)
      return
    }
    const staffName = activeStaff.find((s) => s.id === value)?.name ?? 'this staff member'
    setPendingAdvisorAssign({ staffId: value, staffName })
  }

  const confirmAdvisorAssign = () => {
    if (!pendingAdvisorAssign) return
    assignAdvisor.mutate({ caseId: caseDetail.id, staffId: pendingAdvisorAssign.staffId })
    setPendingAdvisorAssign(null)
  }

  const confirmSendLoginDetails = async () => {
    try {
      const result = await sendLoginDetails.mutateAsync()
      setConfirmingSendLogin(false)
      if (result.emailSent) {
        toast({
          variant: 'success',
          title: `Login credentials sent to ${investor?.email ?? 'the investor'}`,
        })
      } else {
        toast({
          variant: 'warning',
          title:
            "Couldn't email the credentials — the investor's password was still rotated, but they'll need it resent or given to them directly.",
        })
      }
    } catch (error) {
      toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
    }
  }

  return (
    <div className="@4xl/main:h-full flex min-h-0 flex-col gap-4 md:gap-5">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 w-fit shrink-0"
          onClick={() => router.push(backTo)}
        >
          <ArrowLeft className="size-4" /> Back
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {isStaff && !caseDetail.advisor && (
            <Button
              variant="default"
              size="sm"
              className="w-fit shrink-0"
              disabled={claimCase.isPending}
              onClick={() => setConfirmingClaim(true)}
            >
              <UserCheck className="size-3.5" /> Claim this case
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-fit shrink-0"
            disabled={!canPreviewDashboard}
            title={
              !canPreviewDashboard
                ? 'Preview unlocks once this case reaches Audit Ready.'
                : 'See exactly what this client will see'
            }
            onClick={() => {
              // Widened to `string` (not the narrow FlowType union) so the template literal below
              // doesn't collapse into a route-typed literal that typedRoutes then rejects for
              // carrying a query string.
              const flow: string =
                caseDetail.serviceType === 'trades_audit'
                  ? 'trades'
                  : caseDetail.serviceType === 'build_portfolio'
                    ? 'build'
                    : 'audit'
              // Cast: `pathPrefix` union combined with the `flow` query string produces a route
              // template `typedRoutes` can't statically match against `RouteImpl` — same issue as
              // the plain string-literal case, just compounded by the extra union.
              router.push(`${pathPrefix}/${investorId}/preview?flow=${flow}` as Route)
            }}
          >
            <Eye className="size-3.5" /> Preview client dashboard
          </Button>
          {isStaff &&
            isClaimed &&
            (credentialsAlreadySent ? (
              <Button
                variant={caseDetail.loginDetailsLastEmailSent === false ? 'destructive' : 'outline'}
                size="sm"
                className="w-fit shrink-0"
                disabled={caseDetail.caseStatus !== 'published'}
                title={
                  caseDetail.caseStatus !== 'published'
                    ? 'Publish the case first'
                    : !caseDetail.loginDetailsSentAt
                      ? "Already sent for another of this investor's services — resend to issue a new password"
                      : caseDetail.loginDetailsLastEmailSent === false
                        ? `Delivery failed on the last attempt (${formatDateTime(caseDetail.loginDetailsSentAt)}) — resend`
                        : `Sent ${formatDateTime(caseDetail.loginDetailsSentAt)} — resend to rotate the password again`
                }
                onClick={() => setConfirmingSendLogin(true)}
              >
                {caseDetail.loginDetailsLastEmailSent === false ? (
                  <AlertTriangle className="size-3.5" />
                ) : (
                  <Check className="size-3.5" />
                )}{' '}
                Resend login details
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-fit shrink-0"
                disabled={caseDetail.caseStatus !== 'published'}
                title={
                  caseDetail.caseStatus !== 'published'
                    ? 'Publish the case first'
                    : 'Email fresh login credentials to the investor'
                }
                onClick={() => setConfirmingSendLogin(true)}
              >
                <KeyRound className="size-3.5" /> Send login details
              </Button>
            ))}
        </div>
      </div>

      <div className="@4xl/main:grid-cols-[280px_minmax(0,1fr)] @4xl/main:items-stretch @4xl/main:min-h-0 grid min-w-0 flex-1 grid-cols-1 items-start gap-5 overflow-x-hidden">
        {/* Left rail: who this is — fixed identity card, not one that scrolls away with content. */}
        <Card className="@4xl/main:h-full @4xl/main:overflow-y-auto h-fit shrink-0">
          <CardContent className="flex flex-col gap-5">
            <div className="flex items-start justify-between gap-2">
              <div className="@4xl/main:items-start @4xl/main:text-left flex flex-1 flex-col items-center gap-3 text-center">
                <Avatar className="size-16">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold">
                    {initialsOf(investor?.name ?? caseDetail.reference)}
                  </AvatarFallback>
                </Avatar>
                <p className="text-base font-semibold sm:text-lg">{investor?.name ?? '—'}</p>
                <div className="@4xl/main:justify-start flex flex-wrap items-center justify-center gap-1.5">
                  {investorCases.map((c) => (
                    <FlowBadge key={c.id} flow={SERVICE_TYPE_TO_FLOW[c.serviceType]} />
                  ))}
                </div>
              </div>
              {isStaff && isClaimed && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-muted-foreground size-7 shrink-0"
                  aria-label="Edit profile"
                  title={credentialsAlreadySent ? 'Edit investor details' : 'Save investor profile'}
                  onClick={() =>
                    credentialsAlreadySent
                      ? setEditingInvestorDetails(true)
                      : setEditingProfile(true)
                  }
                >
                  <Pencil className="size-3.5" />
                </Button>
              )}
            </div>

            <div className="flex flex-col gap-2 text-xs sm:text-sm">
              {investor?.email && (
                <a
                  href={`mailto:${investor.email}`}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                >
                  <Mail className="size-3.5 shrink-0" />{' '}
                  <span className="truncate">{investor.email}</span>
                </a>
              )}
              {/* `investor?.mobile` — not `caseDetail.contactMobile` — since that's the field
                  `EditInvestorDetailsDialog`/`SaveInvestorProfileDialog` actually edit;
                  `contactMobile` is a frozen snapshot of the number given at case creation and
                  deliberately never changes after that. Falls back to `contactMobile` only for a
                  bare lead with no investor profile saved yet.

                  Staff see this masked (last 4 digits only), so the `tel:` link is disabled for
                  them — a masked number obviously can't be dialed. Staff already have the in-app
                  Plivo "Call" button, which reads the real number server-side and never exposes it
                  to the browser. Admin (`!isStaff`) always sees and can dial the full number. */}
              {isStaff ? (
                <span className="text-muted-foreground flex items-center gap-2">
                  <Phone className="size-3.5 shrink-0" /> +91{' '}
                  {investor?.mobile ?? caseDetail.contactMobile}
                </span>
              ) : (
                <a
                  href={`tel:+91${investor?.mobile ?? caseDetail.contactMobile}`}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                >
                  <Phone className="size-3.5 shrink-0" /> +91{' '}
                  {investor?.mobile ?? caseDetail.contactMobile}
                </a>
              )}
            </div>

            <div className="flex flex-col gap-2 border-t pt-4 text-xs sm:text-sm">
              <p className="text-muted-foreground text-xs font-medium">Client details</p>
              <div className="flex items-center gap-2">
                <CreditCard className="text-muted-foreground size-3.5 shrink-0" />
                <span className="text-muted-foreground">PAN</span>
                <span className="ml-auto font-medium">{investor?.pan || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="text-muted-foreground size-3.5 shrink-0" />
                <span className="text-muted-foreground">City</span>
                <span className="ml-auto font-medium">{investor?.city || '—'}</span>
              </div>
              {/* Only shown once set — unlike PAN/City above, there's no "—" placeholder for an
                  unset age, since most non-Build-Portfolio cases never populate it at all. */}
              {investor?.age != null && (
                <div className="flex items-center gap-2">
                  <Cake className="text-muted-foreground size-3.5 shrink-0" />
                  <span className="text-muted-foreground">Age</span>
                  <span className="ml-auto font-medium">{investor.age}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="text-muted-foreground size-3.5 shrink-0" />
                <span className="text-muted-foreground">Client since</span>
                <span className="ml-auto font-medium">{formatDate(caseDetail.createdAt)}</span>
              </div>
            </div>

            {/* Staff-only (matches its `@Roles('staff')` endpoint) — doesn't replace the
                equivalent list-row control in staff-clients-view.tsx, just adds a second, inline
                place to change it without leaving the case. */}
            {isStaff && isClaimed && (
              <div className="flex items-center gap-2 border-t pt-4 text-xs sm:text-sm">
                <span className="text-muted-foreground">Lead status</span>
                <Select
                  value={caseDetail.seriousnessStatus}
                  onValueChange={(v) =>
                    updateSeriousness.mutate({
                      caseId: caseDetail.id,
                      seriousnessStatus: v as SeriousnessStatus,
                      previousStatus: caseDetail.caseStatus,
                    })
                  }
                >
                  <SelectTrigger size="sm" className="ml-auto w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(LEAD_STATUS_LABEL) as SeriousnessStatus[]).map((status) => (
                      <SelectItem key={status} value={status}>
                        {LEAD_STATUS_LABEL[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Admin-only (matches its `@Roles('admin')` endpoint) — doesn't replace the
                equivalent list-row control in admin-cases-view.tsx, just adds a second, inline
                place to change it without leaving the case. */}
            {!isStaff && (
              <div className="flex items-center gap-2 border-t pt-4 text-xs sm:text-sm">
                <span className="text-muted-foreground">Advisor</span>
                <AdvisorCombobox
                  value={caseDetail.advisor?.id ?? ADVISOR_UNASSIGNED_VALUE}
                  onValueChange={onAdvisorChange}
                  options={activeStaff}
                  className="ml-auto"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: everything else, one tab at a time. The tab bar lives in CardHeader, outside the
            scroll region, so it never moves — only CardContent scrolls. */}
        <Card className="@4xl/main:h-full @4xl/main:min-h-0 @4xl/main:overflow-hidden flex min-w-0 flex-col">
          <Tabs
            value={activeSection}
            onValueChange={handleSectionChange}
            className="flex min-h-0 min-w-0 flex-1 flex-col"
          >
            <CardHeader className="shrink-0">
              {/* One tab per enrolled service (each a separate Case row, including a
                  `build_portfolio` one) plus Overview/Calls — replaces the old separate
                  case-switcher badge row above the tabs; switching cases now happens directly from
                  here via `handleSectionChange`, same `router.push` navigation as before. */}
              {/* Scrolls horizontally below desktop instead of wrapping — `TabsList`'s base class
                  hard-codes a fixed height for horizontal orientation, so a wrapped second row has
                  nowhere to grow into and clips/overlaps the content below it on a narrow screen. */}
              <div className="@4xl/main:mx-0 @4xl/main:overflow-visible @4xl/main:px-0 -mx-1 overflow-x-auto px-1">
                <TabsList className="@4xl/main:w-fit @4xl/main:flex-wrap w-max flex-nowrap">
                  <TabsTrigger value="overview" className="shrink-0 text-xs sm:text-sm">
                    Overview
                  </TabsTrigger>
                  {investorCases.map((c) =>
                    canOpenCase(c) ? (
                      <TabsTrigger key={c.id} value={c.id} className="shrink-0 text-xs sm:text-sm">
                        {SERVICE_TYPE_LABEL[c.serviceType]}
                      </TabsTrigger>
                    ) : (
                      // Disabled, not a normal tab — this case belongs to another staff member (or
                      // is unassigned), and opening it would 404 (`assertCaseAccess` is staff-own-
                      // cases-only). Wrapped in a span, not applied to the trigger directly — a
                      // disabled element doesn't reliably fire hover events (same Radix pattern
                      // `StatusTimeline`'s disabled Publish step already uses).
                      <Tooltip key={c.id}>
                        <TooltipTrigger asChild>
                          <span className="inline-flex shrink-0">
                            <TabsTrigger value={c.id} disabled className="text-xs sm:text-sm">
                              {SERVICE_TYPE_LABEL[c.serviceType]}
                            </TabsTrigger>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{caseOwnerLabel(c)}</TooltipContent>
                      </Tooltip>
                    ),
                  )}
                  <TabsTrigger value="calls" className="shrink-0 text-xs sm:text-sm">
                    Calls{myCalls.length ? ` (${myCalls.length})` : ''}
                  </TabsTrigger>
                </TabsList>
              </div>
            </CardHeader>

            <CardContent className="@4xl/main:min-h-0 @4xl/main:overflow-y-auto min-w-0 flex-1">
              <TabsContent value="overview" className="mt-4 flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-semibold">Services</p>
                  <div className="@lg/main:grid-cols-2 @2xl/main:grid-cols-3 grid grid-cols-1 gap-2">
                    {investorCases.map((c) => {
                      const openable = canOpenCase(c)
                      return (
                        <button
                          key={c.id}
                          type="button"
                          disabled={!openable}
                          onClick={() => openable && handleSectionChange(c.id)}
                          className={cn(
                            'flex min-w-0 flex-col gap-2 rounded-lg border p-3 text-left transition-colors',
                            openable ? 'hover:bg-accent/50' : 'cursor-not-allowed opacity-60',
                          )}
                        >
                          <div className="flex min-w-0 items-center justify-between gap-2">
                            <span className="min-w-0 truncate text-sm font-medium">
                              {SERVICE_TYPE_LABEL[c.serviceType]}
                            </span>
                            <CaseStatusBadge status={c.caseStatus} />
                          </div>
                          {/* Visible without hovering (unlike the compact tab bar's tooltip) —
                              this card already has room for it. */}
                          {!openable && (
                            <span className="text-muted-foreground text-xs">
                              {caseOwnerLabel(c)}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="min-w-0 rounded-lg border p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-sm font-medium">Recommendations</p>
                    {isStaff && isClaimed && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-muted-foreground size-7"
                        aria-label="Recommend a service"
                        title="Recommend a service"
                        onClick={() => setRecommending(true)}
                      >
                        <Sparkles className="size-3.5" />
                      </Button>
                    )}
                  </div>
                  {recommendationsQuery.isError ? (
                    <QueryErrorState
                      error={recommendationsQuery.error}
                      onRetry={() => void recommendationsQuery.refetch()}
                    />
                  ) : recommendationsQuery.isLoading ? (
                    <Skeleton className="h-16 w-full" />
                  ) : recommendationsQuery.data?.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No recommendations yet.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {recommendationsQuery.data?.map((rec) => (
                        <div key={rec.id} className="flex flex-col gap-1.5 rounded-lg border p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-medium">
                              {SERVICE_TYPE_LABEL[rec.serviceType]}
                            </span>
                            <Badge variant={RECOMMENDATION_STATUS_VARIANT[rec.status]}>
                              {RECOMMENDATION_STATUS_LABEL[rec.status]}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground text-sm">{rec.reason}</p>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-muted-foreground text-xs">
                              {rec.recommendedByName ?? 'Staff'} · {formatDate(rec.createdAt)}
                            </span>
                            {isStaff && isClaimed && rec.status === 'open' && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="text-muted-foreground h-7 px-2 text-xs"
                                disabled={updateRecommendationStatus.isPending}
                                onClick={() =>
                                  updateRecommendationStatus.mutate({
                                    id: rec.id,
                                    status: 'dismissed',
                                  })
                                }
                              >
                                <X className="size-3.5" /> Withdraw
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Latest activity</p>
                    <button
                      type="button"
                      className="text-primary text-xs hover:underline"
                      onClick={() => setActiveSection('calls')}
                    >
                      View all
                    </button>
                  </div>
                  {activity.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No calls logged yet.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {activity.map((log) => (
                        <div
                          key={log.id}
                          className="flex items-start gap-2.5 rounded-lg border p-3"
                        >
                          <span className="bg-primary/10 text-primary mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full">
                            <PhoneCall className="size-3.5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm">
                              logged a call —{' '}
                              <span className="text-muted-foreground">
                                {callOutcomeLabel(log.outcome)}
                              </span>
                            </p>
                            {log.notes && (
                              <p className="text-muted-foreground mt-0.5 truncate text-xs">
                                {log.notes}
                              </p>
                            )}
                          </div>
                          <span className="text-muted-foreground shrink-0 text-xs">
                            {formatDateTime(log.startedAt)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value={caseDetail.id} className="mt-4 flex flex-col gap-5">
                {/* Per-service, not per-investor — each case tab shows only its own booked slot,
                    and it disappears once staff advances this case past "Consultation Completed"
                    (no read-only trace kept, by design). */}
                {caseDetail.consultationSlotAt &&
                  CASE_STATUS_PIPELINE.indexOf(caseDetail.caseStatus) <
                    CASE_STATUS_PIPELINE.indexOf('consultation_completed') && (
                    <div className="bg-primary/5 text-primary inline-flex w-fit items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium">
                      <CalendarClock className="size-4 shrink-0" />
                      Consultation slot: {formatDateTime(caseDetail.consultationSlotAt)}
                    </div>
                  )}

                <div className="min-w-0 rounded-lg border p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="text-muted-foreground text-xs">{caseDetail.reference}</span>
                    <span className="text-muted-foreground text-xs">
                      Updated {formatDateTime(caseDetail.updatedAt)}
                    </span>
                  </div>

                  <StatusTimeline
                    status={caseDetail.caseStatus}
                    onSelect={(status) => updateStatus.mutate(status)}
                    canPublish={blockers.length === 0}
                    publishBlockerList={blockers}
                    onPublish={() => publishCase.mutate()}
                    publishing={publishCase.isPending}
                    readOnly={!isStaff || !isClaimed}
                  />
                </div>

                {subResourcesError ? (
                  <QueryErrorState
                    error={subResourcesError}
                    onRetry={() => {
                      void holdingsQuery.refetch()
                      void tradesQuery.refetch()
                      void stepsQuery.refetch()
                      void documentsQuery.refetch()
                      void goalsQuery.refetch()
                    }}
                  />
                ) : subResourcesLoading ? (
                  <Skeleton className="h-40 w-full" />
                ) : (
                  <>
                    <div className="border-t pt-4">
                      {caseDetail.serviceType === 'build_portfolio' ? (
                        <CaseGoals
                          caseId={caseDetail.id}
                          canEdit={isStaff && isClaimed}
                          // Staff and admin both see the Financial Profile card, read-only for
                          // admin — only the investor's own view (elsewhere) omits it entirely.
                          showFinancialProfile
                          goals={goalsQuery.data ?? []}
                          buildProfile={caseDetail.buildProfile}
                          investorAge={investor?.age ?? null}
                          onAddGoal={
                            isStaff && isClaimed
                              ? async (input) => {
                                  await addGoal.mutateAsync(input)
                                }
                              : undefined
                          }
                          onEditGoal={
                            isStaff && isClaimed
                              ? async (id, input) => {
                                  await updateGoal.mutateAsync({ goalId: id, input })
                                }
                              : undefined
                          }
                          onRemoveGoal={
                            isStaff && isClaimed ? (id) => removeGoal.mutate(id) : undefined
                          }
                          onSetGoalApproved={
                            isStaff && isClaimed
                              ? (id, approved) => setGoalApproved.mutate({ goalId: id, approved })
                              : undefined
                          }
                          onAddGoalFund={
                            isStaff && isClaimed
                              ? async (goalId, input) => {
                                  await addGoalFund.mutateAsync({ goalId, input })
                                }
                              : undefined
                          }
                          onUpdateGoalFund={
                            isStaff && isClaimed
                              ? async (goalId, fundId, input) => {
                                  await updateGoalFund.mutateAsync({ goalId, fundId, input })
                                }
                              : undefined
                          }
                          onRemoveGoalFund={
                            isStaff && isClaimed
                              ? async (goalId, fundId) => {
                                  await removeGoalFund.mutateAsync({ goalId, fundId })
                                }
                              : undefined
                          }
                          onUpsertBuildProfile={
                            isStaff && isClaimed
                              ? async (input) => {
                                  await upsertBuildProfile.mutateAsync(input)
                                }
                              : undefined
                          }
                        />
                      ) : (
                        <CaseAnalysis
                          caseRecord={mockCaseRecord}
                          canEdit={isStaff && isClaimed}
                          injectedHoldings={
                            holdingsQuery.data
                              ? toMockHoldings(holdingsQuery.data, caseDetail.id)
                              : []
                          }
                          injectedTrades={
                            tradesQuery.data
                              ? toMockTrades(tradesQuery.data.items, caseDetail.id)
                              : []
                          }
                          onAddHolding={async (input) => {
                            await addHolding.mutateAsync(input)
                          }}
                          onRemoveHolding={(id) => removeHolding.mutate(id)}
                          onEditHolding={async (id, input) => {
                            await updateHolding.mutateAsync({ holdingId: id, input })
                          }}
                          onReplaceHoldings={async (inputs) => {
                            await replaceHoldings.mutateAsync(inputs)
                          }}
                          onAddTrade={async (input) => {
                            await addTrade.mutateAsync(input)
                          }}
                          onRemoveTrade={(id) => removeTrade.mutate(id)}
                          onEditTrade={async (id, input) => {
                            await updateTrade.mutateAsync({ tradeId: id, input })
                          }}
                          onReplaceTrades={async (inputs) => {
                            await replaceTrades.mutateAsync(inputs)
                          }}
                        />
                      )}
                    </div>

                    <div className="border-t pt-4">
                      <CasePrescription
                        caseRecord={mockCaseRecord}
                        canEdit={isStaff && isClaimed}
                        injectedSteps={stepsQuery.data ? toMockSteps(stepsQuery.data) : []}
                        onAddStep={async (input) => {
                          await addStep.mutateAsync(input)
                        }}
                        onEditStep={async (id, input) => {
                          await editStep.mutateAsync({ stepId: id, input })
                        }}
                        onRemoveStep={(id) => removeStep.mutate(id)}
                        onReorderStep={(id, position) =>
                          reorderStep.mutate({ stepId: id, position })
                        }
                        onToggleStepDone={
                          isStaff && isClaimed
                            ? (id, done) => toggleStepDone.mutate({ stepId: id, done })
                            : undefined
                        }
                      />
                    </div>

                    <div className="border-t pt-4">
                      <CaseDocuments
                        canAttachReport={isStaff && isClaimed}
                        injectedDocuments={
                          documentsQuery.data
                            ? toMockDocuments(documentsQuery.data, caseDetail.id)
                            : []
                        }
                        onUpload={async (input): Promise<void> => {
                          await uploadDocument.mutateAsync(input)
                        }}
                        uploadProgress={uploadDocument.progress}
                        isUploading={uploadDocument.isUploading}
                        onCancelUpload={uploadDocument.cancel}
                        // Download/delete/password-reveal are unconditional for admin (unrelated,
                        // pre-existing behavior — admin's own document-review capability isn't part
                        // of this claim gate), but for staff they require real ownership, matching
                        // the backend's `getDocumentDownloadUrl`/`getDocumentPassword` (deliberately
                        // left strict in the read/write split — these expose actual file bytes and a
                        // decrypted CAS password, not just summary figures) and `deleteDocument`
                        // (an ordinary write, same as every other mutation here).
                        onDelete={
                          !isStaff || isClaimed ? (id) => deleteDocument.mutate(id) : undefined
                        }
                        onDownload={
                          !isStaff || isClaimed
                            ? (id) => downloadDocument.mutateAsync(id)
                            : undefined
                        }
                        onGetPassword={
                          !isStaff || isClaimed
                            ? (id) => getDocumentPassword.mutateAsync(id)
                            : undefined
                        }
                      />
                    </div>

                    {/* `CaseUploadLinks` has no canEdit/read-only mode of its own (generate/revoke
                        are always-on once rendered) — hidden entirely until claimed, rather than
                        shown with broken controls. Matches `getUploadLinks` being read-allowed but
                        `createUploadLink`/`revokeUploadLink` staying write-strict server-side. */}
                    {isStaff && isClaimed && (
                      <CaseUploadLinks caseId={caseDetail.id} caseStatus={caseDetail.caseStatus} />
                    )}
                  </>
                )}

                {isStaff && (
                  <div className="min-w-0 rounded-lg border p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="text-sm font-medium">Case details</p>
                      {isClaimed && (
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-muted-foreground size-7"
                          aria-label="Edit case details"
                          onClick={() => setEditingDetails(true)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 text-sm">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-muted-foreground shrink-0">Note</span>
                        <span className="text-right">{caseDetail.note || '—'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-muted-foreground">Next follow-up</span>
                        <span>
                          {caseDetail.nextFollowUpAt ? formatDate(caseDetail.nextFollowUpAt) : '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-muted-foreground">Wealth score</span>
                        <span>{caseDetail.wealthScore ?? '—'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="calls" className="mt-4">
                <div className="flex flex-col gap-6">
                  {isStaff && isClaimed && investor && (
                    <div className="flex flex-col gap-2">
                      <div>
                        <p className="text-sm font-semibold">
                          Call {investor.name ?? 'the investor'}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Start a recorded call with this investor.
                        </p>
                      </div>
                      <DialCallPanel
                        caseId={caseDetail.id}
                        investorName={investor.name}
                        phone={meQuery.data?.phone ?? null}
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="text-sm font-semibold">Calls</p>
                      <p className="text-muted-foreground text-xs">Every call with this client.</p>
                    </div>

                    {callsQuery.isError ? (
                      <QueryErrorState
                        error={callsQuery.error}
                        onRetry={() => void callsQuery.refetch()}
                      />
                    ) : callsQuery.isLoading ? (
                      <Skeleton className="h-24 w-full" />
                    ) : (
                      <CallsList calls={myCalls} caseId={caseDetail.id} />
                    )}
                  </div>
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>

      {isStaff && (
        <>
          <CaseDetailsDialog
            caseDetail={caseDetail}
            open={editingDetails}
            onOpenChange={setEditingDetails}
            mutation={updateCaseDetails}
          />
          <SaveInvestorProfileDialog
            caseDetail={caseDetail}
            open={editingProfile}
            onOpenChange={setEditingProfile}
          />
          <EditInvestorDetailsDialog
            investorId={investor?.id ?? investorId}
            investor={investor}
            open={editingInvestorDetails}
            onOpenChange={setEditingInvestorDetails}
          />
          <RecommendDialog
            open={recommending}
            onOpenChange={setRecommending}
            mutation={createRecommendation}
          />
          <AlertDialog
            open={confirmingSendLogin}
            onOpenChange={(open) => !open && setConfirmingSendLogin(false)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {credentialsAlreadySent ? 'Resend' : 'Send'} login details to{' '}
                  {investor?.email ?? 'this investor'}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This generates a brand-new password and emails it — any password they already have
                  stops working immediately.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={sendLoginDetails.isPending}
                  onClick={() => void confirmSendLoginDetails()}
                >
                  {sendLoginDetails.isPending
                    ? 'Sending…'
                    : credentialsAlreadySent
                      ? 'Resend login details'
                      : 'Send login details'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}

      {!isStaff && (
        <AlertDialog
          open={!!pendingAdvisorAssign}
          onOpenChange={(open) => !open && setPendingAdvisorAssign(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Assign this case to {pendingAdvisorAssign?.staffName}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This replaces any advisor currently assigned to this case.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmAdvisorAssign}>Assign</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {isStaff && (
        <AlertDialog open={confirmingClaim} onOpenChange={setConfirmingClaim}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Claim this case?</AlertDialogTitle>
              <AlertDialogDescription>
                {investor?.name ?? 'This investor'}&apos;s{' '}
                {SERVICE_TYPE_LABEL[caseDetail.serviceType]} case will be assigned to you.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  claimCase.mutate(caseDetail.id)
                  setConfirmingClaim(false)
                }}
              >
                Claim
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
